import React from "react";
import {
  Box,
  Flex,
  FlexItem,
  H3,
  Text,
  Expandable,
  Divider,
  Badge,
  Popover,
} from "orcs-design-system";
import PropTypes from "prop-types";
import styled from "styled-components";
import { isEmpty, cloneDeep } from "lodash";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { getGroupBadgeVariant } from "src/util/group";
import TagBadge from "src/components/Badge/TagBadge";

const ChangeBadge = styled(Box)`
  display: inline-block;
  padding: ${({ theme }) => `${theme.space.xs} ${theme.space.s}`};
  border-radius: ${({ theme }) => theme.radii.s};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  background: ${({ type, theme }) => {
    const colors = {
      added: theme.colors.successDark,
      removed: theme.colors.errorDark,
      moved: theme.colors.warningDark,
      new: theme.colors.successDark,
      changed: theme.colors.infoDark,
      updated: theme.colors.warningDark, // Yellow/warning color for UPDATED
    };
    return colors[type] || theme.colors.border;
  }};
  color: ${({ theme }) => theme.colors.white};
  text-transform: uppercase;
  margin-right: ${({ theme }) => theme.space.s};
`;

const ListItem = styled(Flex)`
  padding: ${({ theme }) => theme.space.s} 0;
  width: 100%;
`;

const SectionWrapper = styled(Box)`
  margin-bottom: ${({ theme }) => theme.space.s};
`;

const PersonBadge = styled(Badge)`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes.s};
`;

const TeamBadge = styled(Badge)`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes.s};
`;

const StrikethroughTeamBadge = styled(TeamBadge)`
  text-decoration: line-through;
  opacity: 0.7;
`;

const InlineBadgeWrapper = styled.span`
  display: inline-block;
  white-space: nowrap;
`;

const TagGroupWrapper = styled(Box)`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.xs};
  align-items: center;
  margin-left: ${({ theme }) => theme.space.s};
`;

const TagGroupLabel = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes.s};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-right: ${({ theme }) => theme.space.xs};
`;

const ChangelogDetails = ({
  entries,
  teams = {},
  filteredTeams = {},
  members = {},
}) => {
  const workspace = useWorkspace();
  const groupTypes = workspace?.config?.groupTypes;
  const tagTypesConfig = workspace?.config?.tagTypes || {};
  const { demandLabel = "demand", supplyLabel = "supply" } =
    workspace?.config?.allocation || {};

  // Helper to get tagTypeConfig for a tag
  const getTagTypeConfig = (tag) => {
    // If tag already has tagTypeConfig, use it
    if (tag?.tagTypeConfig) {
      return tag.tagTypeConfig;
    }
    // Otherwise, look it up from workspace config by tagTypeId
    if (tag?.tagTypeId && tagTypesConfig[tag.tagTypeId]) {
      return tagTypesConfig[tag.tagTypeId];
    }
    return null;
  };

  // Helper to get the label for a team based on its type
  const getTeamTypeLabel = (team) => {
    if (!team || !team.type || !groupTypes) {
      return null;
    }
    const groupType = groupTypes[team.type];
    if (!groupType) {
      return null;
    }
    return groupType.isSupply
      ? supplyLabel
      : groupType.isDemand
      ? demandLabel
      : null;
  };

  const renderEntityName = (entry, sectionKey) => {
    // For teams section, use custom badge with type label as tooltip
    if (sectionKey === "teams" && entry.entity) {
      const typeLabel = getTeamTypeLabel(entry.entity);
      const badgeVariant = getGroupBadgeVariant(entry.entity, groupTypes);
      const badge = (
        <TeamBadge variant={badgeVariant} display="inline-block">
          {entry.entity.name}
        </TeamBadge>
      );

      return (
        <InlineBadgeWrapper>
          {typeLabel ? (
            <Popover direction="top" text={typeLabel}>
              {badge}
            </Popover>
          ) : (
            badge
          )}
        </InlineBadgeWrapper>
      );
    }

    // For members section, use PersonBadge
    if (sectionKey === "members" && entry.entity) {
      const { fte } = entry.entity;
      return (
        <>
          <PersonBadge variant="primaryLight" display="inline-block">
            {entry.entityName}
          </PersonBadge>
          {fte !== null && fte !== undefined && (
            <Text fontSize="s" color="greyDark" as="span" ml="xs">
              ({fte} FTE)
            </Text>
          )}
        </>
      );
    }

    // Fallback to text
    return (
      <Text fontSize="s" color="text" fontWeight="semibold" as="span">
        {entry.entityName}
      </Text>
    );
  };

  const renderTeamName = (teamObj, teamId, teamName) => {
    const team = teamObj || teams[teamId];
    const typeLabel = team ? getTeamTypeLabel(team) : null;

    if (team) {
      const badgeVariant = getGroupBadgeVariant(team, groupTypes);
      const badge = (
        <TeamBadge variant={badgeVariant} display="inline-block">
          {team.name}
        </TeamBadge>
      );

      return (
        <InlineBadgeWrapper>
          {typeLabel ? (
            <Popover direction="top" text={typeLabel}>
              {badge}
            </Popover>
          ) : (
            badge
          )}
        </InlineBadgeWrapper>
      );
    }
    // Team not found (deleted/disabled) - render as badge with strikethrough
    // Use the stored teamName from the entry, which should have been captured
    // when the changelog entry was created
    const displayName = teamName || teamId;
    // Use neutral (grey) variant for deleted/disabled teams
    const badgeVariant = "neutral";
    const badge = (
      <StrikethroughTeamBadge variant={badgeVariant} display="inline-block">
        {displayName}
      </StrikethroughTeamBadge>
    );
    return <InlineBadgeWrapper>{badge}</InlineBadgeWrapper>;
  };

  const renderListItems = (entryList, sectionKey) => {
    if (isEmpty(entryList)) {
      return (
        <Box p="xl">
          <Text color="textSecondary" fontStyle="italic" fontSize="s">
            No changes
          </Text>
        </Box>
      );
    }

    return entryList.map((entry, index) => {
      // Build the main content - badges only, minimal text
      const contentParts = [];

      // Check if this is a tag-only change (no add/move/remove, just tags changed)
      const isTagOnlyChange =
        entry.type === "changed" &&
        sectionKey === "members" &&
        !entry.fromTeam &&
        !entry.toTeam &&
        !entry.from &&
        !entry.to &&
        (entry.added?.length > 0 ||
          entry.removed?.length > 0 ||
          entry.changed?.length > 0);

      // Add change type badge and entity badge
      // For tag-only changes, use "updated" badge instead of "changed"
      contentParts.push(
        <React.Fragment key="badge-name">
          <ChangeBadge
            type={isTagOnlyChange ? "updated" : entry.type}
            as="span"
          >
            {isTagOnlyChange ? "UPDATED" : entry.type}
          </ChangeBadge>
          {renderEntityName(entry, sectionKey)}
        </React.Fragment>
      );

      // For moved/added/removed, show team badges directly without "From:"/"To:" labels
      if (entry.fromTeam || entry.from) {
        contentParts.push(
          <React.Fragment key="from-team">
            <Text as="span" mx="xs" fontSize="s" color="textSecondary">
              →
            </Text>
            {renderTeamName(
              entry.fromTeam,
              entry.from,
              entry.fromName || entry.from
            )}
          </React.Fragment>
        );
      }

      if (entry.toTeam || entry.to) {
        contentParts.push(
          <React.Fragment key="to-team">
            <Text as="span" mx="xs" fontSize="s" color="textSecondary">
              →
            </Text>
            {renderTeamName(entry.toTeam, entry.to, entry.toName || entry.to)}
          </React.Fragment>
        );
      }

      // Add FTE change if applicable
      if (entry.fteChanged) {
        contentParts.push(
          <Text key="fte" fontSize="s" color="textSecondary" as="span" ml="s">
            FTE: {entry.fromFte} → {entry.toFte}
          </Text>
        );
      }

      // Add tag changes if applicable - use TagBadge components
      if (
        (entry.added && entry.added.length > 0) ||
        (entry.removed && entry.removed.length > 0) ||
        (entry.changed && entry.changed.length > 0)
      ) {
        const tagGroups = [];

        // Added tags
        if (entry.added && entry.added.length > 0) {
          tagGroups.push(
            <TagGroupWrapper key="tags-added">
              <TagGroupLabel color="success">Added:</TagGroupLabel>
              {entry.added.map((tag) => {
                // Ensure tag has displayValue for TagBadge
                const tagValue = {
                  ...tag,
                  displayValue:
                    tag.displayValue || tag.name || tag.title || tag.id,
                };
                const tagKey = `added-${
                  tag.id || tag.tagTypeId || tag.displayValue || "unknown"
                }`;
                return (
                  <TagBadge
                    key={tagKey}
                    value={tagValue}
                    withType={true}
                    small={true}
                    tagTypeConfig={getTagTypeConfig(tag)}
                  />
                );
              })}
            </TagGroupWrapper>
          );
        }

        // Removed tags
        if (entry.removed && entry.removed.length > 0) {
          tagGroups.push(
            <TagGroupWrapper key="tags-removed">
              <TagGroupLabel color="error">Removed:</TagGroupLabel>
              {entry.removed.map((tag) => {
                // Ensure tag has displayValue for TagBadge
                const tagValue = {
                  ...tag,
                  displayValue:
                    tag.displayValue || tag.name || tag.title || tag.id,
                };
                const tagKey = `removed-${
                  tag.id || tag.tagTypeId || tag.displayValue || "unknown"
                }`;
                return (
                  <TagBadge
                    key={tagKey}
                    value={tagValue}
                    withType={true}
                    small={true}
                    isRemoved={true}
                    tagTypeConfig={getTagTypeConfig(tag)}
                  />
                );
              })}
            </TagGroupWrapper>
          );
        }

        // Changed tags (show both before and after)
        if (entry.changed && entry.changed.length > 0) {
          tagGroups.push(
            <TagGroupWrapper key="tags-changed">
              <TagGroupLabel color="warning">Changed:</TagGroupLabel>
              {entry.changed.map((change) => {
                const beforeTag = change.before;
                const afterTag = change.after;
                const beforeId =
                  beforeTag?.id ||
                  beforeTag?.tagTypeId ||
                  beforeTag?.name ||
                  beforeTag?.title ||
                  "before";
                const afterId =
                  afterTag?.id ||
                  afterTag?.tagTypeId ||
                  afterTag?.name ||
                  afterTag?.title ||
                  "after";
                const changeKey = `changed-${beforeId}-${afterId}`;
                const beforeValue = {
                  ...beforeTag,
                  displayValue:
                    beforeTag?.displayValue ||
                    beforeTag?.name ||
                    beforeTag?.title ||
                    beforeTag?.id,
                };
                const afterValue = {
                  ...afterTag,
                  displayValue:
                    afterTag?.displayValue ||
                    afterTag?.name ||
                    afterTag?.title ||
                    afterTag?.id,
                };
                return (
                  <React.Fragment key={changeKey}>
                    <TagBadge
                      value={beforeValue}
                      withType={true}
                      small={true}
                      isRemoved={true}
                      tagTypeConfig={
                        getTagTypeConfig(beforeTag) ||
                        getTagTypeConfig(afterTag)
                      }
                    />
                    <Text as="span" mx="xs" fontSize="s" color="textSecondary">
                      →
                    </Text>
                    <TagBadge
                      value={afterValue}
                      withType={true}
                      small={true}
                      tagTypeConfig={
                        getTagTypeConfig(afterTag) ||
                        getTagTypeConfig(beforeTag)
                      }
                    />
                  </React.Fragment>
                );
              })}
            </TagGroupWrapper>
          );
        }

        if (tagGroups.length > 0) {
          contentParts.push(
            <Box key="tag-changes" as="span" display="inline-block" ml="s">
              {tagGroups}
            </Box>
          );
        }
      }

      // Add allocation changes if applicable
      if (
        entry.added &&
        entry.added.length > 0 &&
        entry.entityType !== "team"
      ) {
        const addedAllocs = entry.added
          .map((alloc) => {
            const teamName =
              teams[alloc.targetGroupId]?.name || alloc.targetGroupId;
            const percentage =
              alloc.percentage || alloc.percentageAllocated || "";
            return `${teamName}${percentage ? ` (${percentage}%)` : ""}`;
          })
          .join(", ");
        contentParts.push(
          <Text
            key="allocs-added"
            fontSize="s"
            color="success"
            as="span"
            ml="s"
          >
            +Alloc: {addedAllocs}
          </Text>
        );
      }
      if (
        entry.removed &&
        entry.removed.length > 0 &&
        entry.entityType !== "team"
      ) {
        const removedAllocs = entry.removed
          .map((alloc) => {
            const teamName =
              teams[alloc.targetGroupId]?.name || alloc.targetGroupId;
            const percentage =
              alloc.percentage || alloc.percentageAllocated || "";
            return `${teamName}${percentage ? ` (${percentage}%)` : ""}`;
          })
          .join(", ");
        contentParts.push(
          <Text
            key="allocs-removed"
            fontSize="s"
            color="error"
            as="span"
            ml="s"
          >
            -Alloc: {removedAllocs}
          </Text>
        );
      }
      if (
        entry.changed &&
        entry.changed.length > 0 &&
        entry.entityType !== "team"
      ) {
        const changedAllocs = entry.changed
          .map((change) => {
            const { before } = change;
            const { after } = change;
            const teamName =
              teams[after?.targetGroupId || before?.targetGroupId]?.name ||
              after?.targetGroupId ||
              before?.targetGroupId;
            const beforePct =
              before?.percentage || before?.percentageAllocated || "";
            const afterPct =
              after?.percentage || after?.percentageAllocated || "";
            return `${teamName}${beforePct ? ` ${beforePct}%` : ""} → ${
              afterPct ? `${afterPct}%` : ""
            }`;
          })
          .join(", ");
        contentParts.push(
          <Text
            key="allocs-changed"
            fontSize="s"
            color="warning"
            as="span"
            ml="s"
          >
            Alloc: {changedAllocs}
          </Text>
        );
      }

      // Date on the right - use changeDate if available, otherwise use a placeholder
      const dateText = entry.changeDate ? (
        <Text fontSize="xs" color="greyDark" fontStyle="normal">
          {entry.changeDate}
        </Text>
      ) : (
        <Text fontSize="xs" color="textSecondary" fontStyle="italic">
          -
        </Text>
      );

      return (
        <React.Fragment
          key={`${entry.entityId}-${entry.type}-${entry.from || ""}-${
            entry.to || ""
          }`}
        >
          <ListItem alignItems="center">
            <FlexItem
              flex="1 1 auto"
              style={{ minWidth: 0, overflow: "hidden" }}
            >
              <Box as="span" display="inline-block" width="100%">
                {contentParts}
              </Box>
            </FlexItem>
            <FlexItem
              flex="0 0 auto"
              pl="l"
              style={{ minWidth: "120px", textAlign: "right", flexShrink: 0 }}
            >
              {dateText}
            </FlexItem>
          </ListItem>
          {index < entryList.length - 1 && <Divider light />}
        </React.Fragment>
      );
    });
  };

  // Helper function to check if a team is hidden/deleted
  const isTeamHiddenOrDeleted = (teamId) => {
    const team = teams[teamId];
    return team && (team.isHidden || team.isDeleted);
  };

  // Process tag changes for teams and members - add them to their respective sections
  // Entries are already filtered by parent component (hidden/deleted teams excluded)
  // Deep clone arrays to avoid mutating the original entries
  const teamsWithTags = cloneDeep(entries.teams || []);
  const membersWithTags = cloneDeep(entries.members || []);
  const membersWithAllocations = cloneDeep(membersWithTags);

  // Add tag changes to teams section (exclude hidden/deleted teams)
  (entries.tags || [])
    .filter((tagEntry) => tagEntry.entityType === "team")
    .filter((tagEntry) => {
      // Don't add if team is hidden/deleted
      if (isTeamHiddenOrDeleted(tagEntry.entityId)) {
        return false;
      }
      return true;
    })
    .forEach((tagEntry) => {
      // Check if team entry already exists
      const existingTeamEntry = teamsWithTags.find(
        (e) => e.entityId === tagEntry.entityId
      );
      if (existingTeamEntry) {
        // Merge tag changes into existing entry - create new object to avoid mutation
        const index = teamsWithTags.indexOf(existingTeamEntry);
        teamsWithTags[index] = {
          ...existingTeamEntry,
          added: [
            ...(existingTeamEntry.added || []),
            ...(tagEntry.added || []),
          ],
          removed: [
            ...(existingTeamEntry.removed || []),
            ...(tagEntry.removed || []),
          ],
          changed: [
            ...(existingTeamEntry.changed || []),
            ...(tagEntry.changed || []),
          ],
        };
      } else {
        // Create new entry for tag-only changes
        const team =
          teams[tagEntry.entityId] || filteredTeams[tagEntry.entityId];
        if (!team || (!team.isHidden && !team.isDeleted)) {
          teamsWithTags.push({
            ...tagEntry,
            type: "changed",
            entity: team,
          });
        }
      }
    });

  // Add tag changes to members section (only if not filtered)
  (entries.tags || [])
    .filter((tagEntry) => tagEntry.entityType === "member")
    .forEach((tagEntry) => {
      // Check if member entry already exists
      const existingMemberEntry = membersWithAllocations.find(
        (e) => e.entityId === tagEntry.entityId
      );
      if (existingMemberEntry) {
        // Merge tag changes into existing entry - create new object to avoid mutation
        const index = membersWithAllocations.indexOf(existingMemberEntry);
        membersWithAllocations[index] = {
          ...existingMemberEntry,
          added: [
            ...(existingMemberEntry.added || []),
            ...(tagEntry.added || []),
          ],
          removed: [
            ...(existingMemberEntry.removed || []),
            ...(tagEntry.removed || []),
          ],
          changed: [
            ...(existingMemberEntry.changed || []),
            ...(tagEntry.changed || []),
          ],
        };
      } else {
        // Create new entry for tag-only changes
        membersWithAllocations.push({
          ...tagEntry,
          type: "changed",
          entity: members[tagEntry.entityId],
        });
      }
    });

  // Add allocation changes to members section (only if not filtered)
  (entries.allocations || []).forEach((allocEntry) => {
    // Check if member entry already exists
    const existingMemberEntry = membersWithAllocations.find(
      (e) => e.entityId === allocEntry.entityId
    );
    if (existingMemberEntry) {
      // Merge allocation changes into existing entry - create new object to avoid mutation
      const index = membersWithAllocations.indexOf(existingMemberEntry);
      membersWithAllocations[index] = {
        ...existingMemberEntry,
        added: [
          ...(existingMemberEntry.added || []),
          ...(allocEntry.added || []),
        ],
        removed: [
          ...(existingMemberEntry.removed || []),
          ...(allocEntry.removed || []),
        ],
      };
      existingMemberEntry.changed = [
        ...(existingMemberEntry.changed || []),
        ...(allocEntry.changed || []),
      ];
      if (allocEntry.fteChanged) {
        existingMemberEntry.fteChanged = true;
        existingMemberEntry.fromFte = allocEntry.fromFte;
        existingMemberEntry.toFte = allocEntry.toFte;
      }
    } else {
      // Create new entry for allocation-only changes
      membersWithAllocations.push({
        ...allocEntry,
        type: "changed",
        entity: members[allocEntry.entityId],
      });
    }
  });

  const sections = [
    {
      key: "teams",
      title: "Structures",
      entries: teamsWithTags,
    },
    {
      key: "members",
      title: "Members",
      entries: membersWithAllocations,
    },
  ];

  return (
    <Box>
      <Flex alignItems="center" gap="m" mb="m">
        <H3 fontSize="l" fontWeight="bold" m={0}>
          Detailed Changes
        </H3>
      </Flex>
      <Divider mb="m" />
      {sections.map((section) => {
        const hasEntries = !isEmpty(section.entries);
        const sectionTitle = hasEntries
          ? `${section.title} (${section.entries.length})`
          : section.title;

        return (
          <SectionWrapper key={section.key}>
            <Expandable title={sectionTitle} small>
              {renderListItems(section.entries, section.key)}
            </Expandable>
          </SectionWrapper>
        );
      })}
    </Box>
  );
};

ChangelogDetails.propTypes = {
  entries: PropTypes.object,
  teams: PropTypes.object,
  filteredTeams: PropTypes.object,
  members: PropTypes.object,
};

export default ChangelogDetails;
